import type { ServerError } from '@apollo/client';
import type { ApolloError } from '@apollo/client/errors';
import type { GraphQLFormattedError } from 'graphql';

// generic message to display if an internal (GRPC 13) error occurs.
// The UI should never expose errors that have the INTERNAL code as they could reveal internals of our infrastructure and architecture not meant to be exposed to public customers.
export const genericErrorMessage = 'Oops, something went wrong.';

// generic message to display if a user does not have permission to perform an action.
export const genericPermissionErrorMessage = `You don't have permission to complete this action.`;

function isServerError(error: unknown): error is ServerError {
  return Boolean(error && typeof error === 'object' && (error as ServerError).statusCode);
}

export function parseGraphqlError(err: ApolloError): string[] {
  if (err.networkError && isServerError(err.networkError) && err.networkError.statusCode === 403) {
    return [genericPermissionErrorMessage];
  } else if (err.graphQLErrors?.length > 0) {
    // If we have graphql errors, let's handle those
    const errors: string[] = [];
    err.graphQLErrors.forEach((gqlErr: GraphQLFormattedError<Record<string, any>>) => {
      // Strip out the prefix if it exists
      const matches = gqlErr?.message.match(/^(\d{1,2}[ ]{1}[^:]*):(.*)/);
      const isInternalError = gqlErr?.message.includes('13'); // gRPC Internal error code

      const msg = isInternalError ? genericErrorMessage : matches?.[2] ?? gqlErr.message;

      errors.push(msg.trim());
    });
    return errors;
  } else {
    // All other errors (network, etc) get distilled into the err
    return [err.message];
  }
}
